import useMediaQuery from '@material-ui/core/useMediaQuery';
import React, { useMemo, useState } from 'react';
import { Atividade } from '../../models/atividade';
import { useMaestroContext } from '../../context/MaestroContext';
import { calcularPrazoRelativo, ehDiaUtil, ehFimDeSemana } from '../../utils/businessDays';
import { formatarData } from '../../utils/formatters';
import { LABEL_STATUS_CICLO } from '../../utils/labels';
import Card from '../Card';
import StatusBadge from '../StatusBadge';

const NOMES_DIA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const NOMES_MES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const PRAZOS_RELATIVOS: Array<'D-5' | 'D-3' | 'D-1' | 'D+1' | 'D+2' | 'D+5'> = ['D-5', 'D-3', 'D-1', 'D+1', 'D+2', 'D+5'];
const COR_FALLBACK_AREA = '#5b6b78';

const pad = (n: number) => String(n).padStart(2, '0');
const chaveMes = (ano: number, mes: number) => `${ano}-${pad(mes)}`;
const mesAnterior = (ano: number, mes: number): [number, number] => (mes === 1 ? [ano - 1, 12] : [ano, mes - 1]);
const proximoMes = (ano: number, mes: number): [number, number] => (mes === 12 ? [ano + 1, 1] : [ano, mes + 1]);
const isoParaTempo = (iso: string) => new Date(iso.split('T')[0]).getTime();

const VisaoCalendario: React.FC = () => {
  const { cicloAtivo, atividades, feriados, getArea, getUsuario, abrirAtividade } = useMaestroContext();
  const mobile = useMediaQuery('(max-width: 600px)');

  const [anoCompetencia, mesCompetencia] = cicloAtivo.prazoOficial.split('T')[0].split('-').map(Number);
  const chaveCompetenciaAtual = chaveMes(anoCompetencia, mesCompetencia);

  const [mesRef, setMesRef] = useState<{ ano: number; mes: number }>({ ano: anoCompetencia, mes: mesCompetencia });
  const [diaSelecionado, setDiaSelecionado] = useState<string | null>(null);

  const { ano, mes } = mesRef;
  const chaveAtual = chaveMes(ano, mes);
  const diasNoMes = new Date(ano, mes, 0).getDate();
  const primeiroDiaSemana = new Date(ano, mes - 1, 1).getDay();
  // O "fechamento" real do mês é o status do próprio ciclo (RN-25/RN-39) — não existe conceito de
  // calendário fechado separado do ciclo no backend, então este calendário só reflete o status real
  // do ciclo cuja competência bate com o mês em exibição (via Header/Cockpit é onde se encerra/reabre).
  const ehMesDoCicloAtivo = chaveAtual === chaveCompetenciaAtual;

  const tarefas = useMemo(
    () => atividades.filter(a => a.nivelHierarquico === 'TAREFA' && a.dataPlanejada && a.dataPrevista),
    [atividades]
  );

  const atividadesPorDia = useMemo(() => {
    const mapa = new Map<string, Atividade[]>();
    // dataPrevista vem como ISO completo do backend — sem o split('T')[0], a chave nunca batia com
    // o "iso" (YYYY-MM-DD) de cada célula do calendário e "X ativ." nunca aparecia em dia nenhum.
    tarefas.forEach(a => {
      const iso = a.dataPrevista.split('T')[0];
      mapa.set(iso, [...(mapa.get(iso) ?? []), a]);
    });
    return mapa;
  }, [tarefas]);

  // Sequência real de áreas do ciclo — do dataPlanejada mais cedo ao dataPrevista mais tarde de cada
  // área, ordenada por início. É o "drill-down" pedido: quando cada área começa e onde ela entrega a
  // bola pra próxima, sem inventar nenhuma data — tudo vem das atividades reais do ciclo.
  const sequenciaAreas = useMemo(() => {
    const porArea = new Map<number, Atividade[]>();
    tarefas.forEach(a => porArea.set(a.areaId, [...(porArea.get(a.areaId) ?? []), a]));

    const linhas = Array.from(porArea.entries()).map(([areaId, itens]) => {
      const inicio = itens.reduce((min, a) => (a.dataPlanejada < min ? a.dataPlanejada : min), itens[0].dataPlanejada).split('T')[0];
      const fim = itens.reduce((max, a) => (a.dataPrevista > max ? a.dataPrevista : max), itens[0].dataPrevista).split('T')[0];
      const area = getArea(areaId);
      const concluidas = itens.filter(a => a.status === 'CONCLUIDA').length;
      return {
        areaId,
        nome: area ? area.nome : `Área #${areaId}`,
        cor: area?.cor || COR_FALLBACK_AREA,
        inicio,
        fim,
        quantidade: itens.length,
        concluidas,
      };
    });

    linhas.sort((a, b) => (a.inicio === b.inicio ? (a.fim < b.fim ? -1 : 1) : a.inicio < b.inicio ? -1 : 1));

    if (linhas.length === 0) return { linhas: [], inicioGeral: 0, fimGeral: 0 };
    const inicioGeral = Math.min(...linhas.map(l => isoParaTempo(l.inicio)));
    const fimGeral = Math.max(...linhas.map(l => isoParaTempo(l.fim)));
    return { linhas, inicioGeral, fimGeral };
  }, [tarefas, getArea]);

  const dias = Array.from({ length: diasNoMes }, (_, i) => {
    const dia = i + 1;
    const iso = `${ano}-${pad(mes)}-${pad(dia)}`;
    const atividadesDoDia = atividadesPorDia.get(iso) ?? [];
    const areasDoDia = Array.from(new Set(atividadesDoDia.map(a => a.areaId)))
      .map(id => getArea(id))
      .filter((a): a is NonNullable<typeof a> => !!a);
    return {
      dia,
      iso,
      fimDeSemana: ehFimDeSemana(iso),
      feriado: feriados.find(f => f.data.split('T')[0] === iso),
      diaUtil: ehDiaUtil(iso, feriados),
      atividadesDoDia,
      areasDoDia,
      ehPrazoOficial: iso === cicloAtivo.prazoOficial,
    };
  });

  const prazosRelativos = PRAZOS_RELATIVOS.map(tipo => ({ tipo, ...calcularPrazoRelativo(cicloAtivo.prazoOficial, tipo, feriados) }));

  const diaDetalhado = diaSelecionado ? dias.find(d => d.iso === diaSelecionado) : undefined;
  const gruposDoDiaDetalhado = useMemo(() => {
    if (!diaDetalhado) return [];
    const porArea = new Map<number, Atividade[]>();
    diaDetalhado.atividadesDoDia.forEach(a => porArea.set(a.areaId, [...(porArea.get(a.areaId) ?? []), a]));
    return Array.from(porArea.entries())
      .map(([areaId, itens]) => ({ areaId, area: getArea(areaId), itens }))
      .sort((x, y) => (x.area?.nome ?? '').localeCompare(y.area?.nome ?? ''));
  }, [diaDetalhado, getArea]);

  const selecionarDia = (iso: string) => setDiaSelecionado(prev => (prev === iso ? null : iso));

  return (
    <Card title='Calendário de Fechamento' subtitle='Navegue mês a mês — dias úteis, feriados, prazos relativos e a sequência real das áreas do ciclo. Clique em um dia para ver o drill-down.'>
      <div className='mc-cal-nav'>
        <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => setMesRef({ ano: mesAnterior(ano, mes)[0], mes: mesAnterior(ano, mes)[1] })}>
          <i className='bi bi-chevron-left' />
        </button>
        <div className='mc-cal-nav__titulo'>
          <strong>{NOMES_MES[mes - 1]}/{ano}</strong>
          {ehMesDoCicloAtivo && <span className={`mc-badge mc-badge--${cicloAtivo.status === 'ENCERRADO' ? 'neutro' : 'success'}`}>{LABEL_STATUS_CICLO[cicloAtivo.status]}</span>}
          {ehMesDoCicloAtivo && <span className='mc-badge mc-badge--info'>Competência do ciclo atual</span>}
        </div>
        <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => setMesRef({ ano: proximoMes(ano, mes)[0], mes: proximoMes(ano, mes)[1] })}>
          <i className='bi bi-chevron-right' />
        </button>
      </div>

      {mobile ? (
        <div className='mc-agenda'>
          {dias.filter(d => d.atividadesDoDia.length > 0 || d.feriado || d.ehPrazoOficial).map(d => (
            <div className='mc-agenda__item' key={d.iso} onClick={() => selecionarDia(d.iso)}>
              <strong>{pad(d.dia)}/{pad(mes)}</strong>
              <div>
                {d.feriado && <span className='mc-badge mc-badge--neutro'>{d.feriado.nome}</span>}
                {d.ehPrazoOficial && <span className='mc-badge mc-badge--danger'>Prazo oficial</span>}
                {d.atividadesDoDia.length > 0 && <span className='mc-badge mc-badge--info'>{d.atividadesDoDia.length} atividade(s)</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className='mc-calendario'>
          {NOMES_DIA.map((n, i) => <div className='mc-calendario__cabecalho' key={`cab-${i}`}>{n}</div>)}
          {Array.from({ length: primeiroDiaSemana }).map((_, i) => <div className='mc-calendario__vazio' key={`vazio-${i}`} />)}
          {dias.map(d => (
            <button
              type='button'
              key={d.iso}
              className={`mc-calendario__dia${d.fimDeSemana ? ' is-fim-de-semana' : ''}${d.feriado ? ' is-feriado' : ''}${d.ehPrazoOficial ? ' is-prazo' : ''}${diaSelecionado === d.iso ? ' is-selecionado' : ''}`}
              title={d.feriado?.nome}
              onClick={() => selecionarDia(d.iso)}
            >
              <span>{d.dia}</span>
              {d.atividadesDoDia.length > 0 && <small>{d.atividadesDoDia.length} ativ.</small>}
              {d.areasDoDia.length > 0 && (
                <div className='mc-calendario__dots'>
                  {d.areasDoDia.slice(0, 4).map(a => <span key={a.id} className='mc-calendario__dot' style={{ background: a.cor || COR_FALLBACK_AREA }} title={a.nome} />)}
                </div>
              )}
              {d.ehPrazoOficial && <small className='mc-calendario__tag'>Prazo</small>}
            </button>
          ))}
        </div>
      )}

      {diaDetalhado && (
        <div className='mc-cal-drilldown'>
          <h3>
            Atividades previstas para {formatarData(diaDetalhado.iso)}
            <button type='button' className='mc-btn mc-btn--secondary mc-btn--sm' onClick={() => setDiaSelecionado(null)}>Fechar</button>
          </h3>
          {gruposDoDiaDetalhado.length === 0 ? (
            <p className='mc-texto-neutro'>Nenhuma atividade com prazo previsto para este dia.</p>
          ) : (
            gruposDoDiaDetalhado.map(({ areaId, area, itens }) => (
              <div className='mc-cal-drilldown__grupo' key={areaId}>
                <div className='mc-cal-drilldown__area'>
                  <span className='mc-calendario__dot' style={{ background: area?.cor || COR_FALLBACK_AREA }} />
                  <strong>{area ? area.nome : `Área #${areaId}`}</strong>
                  <span className='mc-texto-neutro'>{itens.length} atividade(s)</span>
                </div>
                <table className='mc-cal-drilldown__tabela'>
                  <tbody>
                    {itens.map(a => {
                      const responsavel = getUsuario(a.responsavelId);
                      return (
                        <tr key={a.id} onClick={() => abrirAtividade(a.id)}>
                          <td>{a.nome}</td>
                          <td>{responsavel && <span className='mc-avatar' title={responsavel.nome}>{responsavel.iniciais}</span>}<span className='mc-cal-drilldown__responsavel'>{responsavel?.nome ?? '—'}</span></td>
                          <td><StatusBadge status={a.status} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>
      )}

      <div className='mc-area-timeline'>
        <h3>Sequência das áreas no ciclo</h3>
        {sequenciaAreas.linhas.length === 0 ? (
          <p className='mc-texto-neutro'>Nenhuma atividade com data planejada/prevista neste ciclo ainda.</p>
        ) : (
          <div className='mc-area-timeline__lista'>
            {sequenciaAreas.linhas.map(l => {
              const spanTotal = Math.max(sequenciaAreas.fimGeral - sequenciaAreas.inicioGeral, 1);
              const left = ((isoParaTempo(l.inicio) - sequenciaAreas.inicioGeral) / spanTotal) * 100;
              const width = Math.max(((isoParaTempo(l.fim) - isoParaTempo(l.inicio)) / spanTotal) * 100, 2);
              return (
                <div className='mc-area-timeline__row' key={l.areaId}>
                  <div className='mc-area-timeline__label'>
                    <span className='mc-calendario__dot' style={{ background: l.cor }} />
                    {l.nome}
                  </div>
                  <div className='mc-area-timeline__trilha'>
                    <div className='mc-area-timeline__barra' style={{ left: `${left}%`, width: `${width}%`, background: l.cor }} title={`${l.nome}: ${formatarData(l.inicio)} a ${formatarData(l.fim)}`}>
                      <span>{l.concluidas}/{l.quantidade}</span>
                    </div>
                  </div>
                  <div className='mc-area-timeline__datas'>{formatarData(l.inicio)} – {formatarData(l.fim)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className='mc-prazos-relativos'>
        <h3>Prazos relativos ao encerramento oficial</h3>
        <div className='mc-prazos-relativos__grid'>
          {prazosRelativos.map(p => (
            <div className='mc-prazos-relativos__item' key={p.tipo}>
              <strong>{p.tipo}</strong>
              <span>{formatarData(p.dataAjustada)}</span>
              {p.motivo && <small>{p.motivo} — ajustado de {formatarData(p.dataOriginal)}</small>}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default VisaoCalendario;
